import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { t } from '../../../utils/translations';
import { formatDateTime } from '../../../utils/dateUtils';

const PublicationControls = ({ 
  settings, 
  onSave, 
  onPreview, 
  onPublish, 
  onSchedule,
  isSaving,
  hasUnsavedChanges 
}) => {
  const [showScheduleOptions, setShowScheduleOptions] = useState(false);

  const getPublishButtonText = () => {
    switch (settings?.status) {
      case 'published':
        return t('articleEditor.updateArticle');
      case 'scheduled':
        return t('articleEditor.updateSchedule');
      case 'review':
        return t('articleEditor.submitForReview');
      default:
        return t('articleEditor.publishArticle');
    }
  };

  const getPublishButtonVariant = () => {
    switch (settings?.status) {
      case 'published':
        return 'secondary';
      case 'scheduled':
        return 'warning';
      case 'review':
        return 'accent';
      default:
        return 'default';
    }
  };

  const handleScheduleClick = () => {
    if (settings?.publishDate) {
      onSchedule();
    } else {
      setShowScheduleOptions(true);
    }
  };

  return (
    <div className="space-y-4">
      {/* Status Indicator */}
      <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${
            settings?.status === 'published' ? 'bg-success' :
            settings?.status === 'scheduled' ? 'bg-warning' :
            settings?.status === 'review'? 'bg-accent' : 'bg-muted-foreground'
          }`} />
          <span className="text-sm font-medium text-foreground">
            {t('articleEditor.status')}: {settings?.status === 'published' ? t('common.published') : settings?.status === 'scheduled' ? t('articleEditor.scheduled') : settings?.status === 'review' ? t('common.draft') : t('common.draft')}
          </span>
        </div>
        
        {settings?.publishDate && (
          <div className="text-xs text-muted-foreground">
            {t('articleEditor.scheduled')}: {formatDateTime(new Date(settings.publishDate))}
          </div>
        )}
      </div>
      {/* Primary Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          variant="outline"
          onClick={onSave}
          loading={isSaving}
          disabled={!hasUnsavedChanges}
          iconName="Save"
          iconPosition="left"
          iconSize={16}
          fullWidth
        >
          {t('articleEditor.saveDraft')}
        </Button>

        <Button
          variant="secondary"
          onClick={onPreview}
          iconName="Eye"
          iconPosition="left"
          iconSize={16}
          fullWidth
        >
          {t('articleEditor.preview')}
        </Button>
      </div>
      {/* Publication Actions */}
      <div className="space-y-2">
        <Button
          variant={getPublishButtonVariant()}
          onClick={onPublish}
          iconName={settings?.status === 'published' ? 'RefreshCw' : 'Send'}
          iconPosition="left"
          iconSize={16}
          fullWidth
        >
          {getPublishButtonText()}
        </Button>

        {settings?.status !== 'published' && (
          <div className="relative">
            <Button
              variant="outline"
              onClick={handleScheduleClick}
              iconName="Calendar"
              iconPosition="left"
              iconSize={16}
              fullWidth
            >
              {settings?.publishDate ? t('articleEditor.updateScheduleButton') : t('articleEditor.schedulePublication')}
            </Button>

            {showScheduleOptions && (
              <div className="absolute top-full left-0 right-0 mt-2 p-4 bg-popover border border-border rounded-lg shadow-elevation-2 z-10">
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-popover-foreground">
                    {t('articleEditor.schedulePublication')}
                  </h4>
                  
                  <input
                    type="datetime-local"
                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm"
                    min={new Date()?.toISOString()?.slice(0, 16)}
                    onChange={(e) => {
                      // Handle schedule date change
                      console.log('Schedule date:', e?.target?.value);
                    }}
                  />
                  
                  <div className="flex space-x-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => {
                        onSchedule();
                        setShowScheduleOptions(false);
                      }}
                      fullWidth
                    >
                      {t('articleEditor.schedule')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowScheduleOptions(false)}
                      fullWidth
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Additional Actions */}
      <div className="pt-4 border-t border-border space-y-2">
        <Button
          variant="ghost"
          size="sm"
          iconName="Copy"
          iconPosition="left"
          iconSize={16}
          fullWidth
        >
          {t('articleEditor.duplicateArticle')}
        </Button>

        <Button
          variant="ghost"
          size="sm"
          iconName="Archive"
          iconPosition="left"
          iconSize={16}
          fullWidth
        >
          {t('articleEditor.moveToArchive')}
        </Button>

        {settings?.status === 'published' && (
          <Button
            variant="ghost"
            size="sm"
            iconName="EyeOff"
            iconPosition="left"
            iconSize={16}
            fullWidth
          >
            {t('articleEditor.unpublish')}
          </Button>
        )}

        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          iconName="Trash2"
          iconPosition="left"
          iconSize={16}
          fullWidth
        >
          {t('articleEditor.deleteArticle')}
        </Button>
      </div>
      {/* Validation Messages */}
      {hasUnsavedChanges && (
        <div className="flex items-center space-x-2 p-3 bg-warning/10 border border-warning/20 rounded-lg">
          <Icon name="AlertTriangle" size={16} className="text-warning" />
          <span className="text-sm text-warning">
            {t('articleEditor.unsavedChanges')}
          </span>
        </div>
      )}
    </div>
  );
};

export default PublicationControls;